import React, { useState, useEffect } from 'react'
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Stack
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'

export default function PasswordRecovery({
  setSnackbarSeverity,
  setErrorMessage,
  setShowError,
  envConfig
}) {
  const theme = useTheme()

  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false)
  const [codeDialogOpen, setCodeDialogOpen] = useState(false)
  const [recoveryEmail, setRecoveryEmail] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [recoveryLoading, setRecoveryLoading] = useState(false)
  const [emailPreloaded, setEmailPreloaded] = useState(false)

  const buttonLogInCSS = {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const firstCode = params.get('firstCode')
    const secondCode = params.get('secondCode')
    const email = params.get('email')
    if (firstCode && secondCode && email) {
      setRecoveryEmail(email)
      setRecoveryCode(firstCode + secondCode)
      setEmailPreloaded(true)
      setCodeDialogOpen(true)
    }
  }, [])

  // Expose these methods via an event or ref if the parent needs them,
  // or we can just listen to a global event. 
  // Wait, how does the parent trigger `handleOpenRecovery`?
  // We can expose it by moving the "Forgot password" link into this component,
  // or passing a prop. Wait! Let's just have the button/link inside this component if possible.
  // Let's pass `ref` or just expose a button renderer.
  // Actually, the parent can just pass a render prop or we can export a component that includes the "forgot password" link.

  const handleOpenRecovery = () => {
    setRecoveryEmail('')
    setEmailPreloaded(false)
    setRecoveryDialogOpen(true)
  }

  const handleCloseDialogs = () => {
    setRecoveryDialogOpen(false)
    setCodeDialogOpen(false)
    setRecoveryEmail('')
    setRecoveryCode('')
    setNewPassword('')
    setConfirmPassword('')
    setRecoveryLoading(false)
    setEmailPreloaded(false)
  }

  const handleRecoveryRequest = async () => {
    if (!recoveryEmail) return

    setRecoveryLoading(true)

    await fetch(`${envConfig.url}/api/auth/recovery`, {
      method: 'POST',
      headers: {
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: recoveryEmail })
    })
      .then(res => res.json())
      .then(data => {
        if (data && (data.message === 'ok' || data.status === 'Correo enviado')) {
          setEmailPreloaded(true)
          setRecoveryDialogOpen(false)
          setCodeDialogOpen(true)
          setSnackbarSeverity('success')
          setErrorMessage('Si la cuenta existe, recibirá un correo con instrucciones para restablecer su contraseña.')
          setShowError(true)
        } else {
          setSnackbarSeverity('error')
          setErrorMessage(data && data.error ? data.error : 'Error al enviar la solicitud de recuperación.')
          setShowError(true)
        }
      })
      .catch(error => {
        setSnackbarSeverity('error')
        setErrorMessage('Ocurrió un problema de conexión al enviar la solicitud. Por favor, verifique su red o intente de nuevo más tarde.')
        setShowError(true)
      })

    setRecoveryLoading(false)
  }

  const handleResetPassword = async () => {
    if (!recoveryEmail || !recoveryCode || !newPassword || !confirmPassword) return

    if (newPassword.length < 8) {
      setSnackbarSeverity('error')
      setErrorMessage('La contraseña debe tener al menos 8 caracteres.')
      setShowError(true)
      return
    }

    if (newPassword !== confirmPassword) {
      setSnackbarSeverity('error')
      setErrorMessage('Las contraseñas no coinciden.')
      setShowError(true)
      return
    }

    setRecoveryLoading(true)

    await fetch(`${envConfig.url}/api/auth/recovery`, {
      method: 'PATCH',
      headers: {
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: recoveryEmail,
        code: recoveryCode,
        password: newPassword,
        password_confirmation: confirmPassword
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data && data.message === 'ok') {
          setSnackbarSeverity('success')
          setErrorMessage('Su contraseña ha sido restablecida exitosamente.')
          setShowError(true)
          handleCloseDialogs()
        } else {
          setSnackbarSeverity('error')
          setErrorMessage(data && data.error ? data.error : 'Error al restablecer la contraseña.')
          setShowError(true)
        }
      })
      .catch(error => {
        setSnackbarSeverity('error')
        setErrorMessage('Ocurrió un problema de conexión al restablecer la contraseña. Por favor, verifique su red o intente de nuevo más tarde.')
        setShowError(true)
      })

    setRecoveryLoading(false)
  }

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        sx={{ mt: 1 }}
        justifyContent={{ md: 'center' }}
      >
        <Button
          variant='text'
          onClick={handleOpenRecovery}
          sx={{ textTransform: 'none', justifyContent: 'center' }}
        >
          Olvidé mi contraseña
        </Button>
        <Button
          variant='text'
          onClick={() => {
            setEmailPreloaded(false)
            setCodeDialogOpen(true)
          }}
          sx={{ textTransform: 'none', justifyContent: 'center' }}
        >
          Ya tengo un código de recuperación
        </Button>
      </Stack>

      <Dialog
        open={recoveryDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Recuperar Contraseña</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Ingrese su correo electrónico para recibir un código de recuperación.</DialogContentText>
          <TextField
            autoFocus
            label='Correo electrónico'
            type='email'
            fullWidth
            variant='outlined'
            value={recoveryEmail}
            onChange={e => setRecoveryEmail(e.target.value)}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogs}
            color='primary'
          >
            Cerrar
          </Button>
          <Button
            onClick={handleRecoveryRequest}
            variant='contained'
            sx={buttonLogInCSS}
            disabled={recoveryLoading || !recoveryEmail}
          >
            {recoveryLoading ? <CircularProgress size={24} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={codeDialogOpen}
        onClose={handleCloseDialogs}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Restablecer Contraseña</DialogTitle>
        <DialogContent>
          <TextField
            label='Correo electrónico'
            type='email'
            fullWidth
            variant='outlined'
            value={recoveryEmail}
            onChange={e => setRecoveryEmail(e.target.value)}
            disabled={emailPreloaded}
            inputProps={{ maxLength: 100 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label='Código de recuperación'
            fullWidth
            variant='outlined'
            value={recoveryCode}
            onChange={e => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
              setRecoveryCode(val)
            }}
            inputProps={{ maxLength: 10 }}
            sx={{ mb: 2 }}
          />
          <FormControl
            variant='outlined'
            fullWidth
            sx={{ mb: 2 }}
          >
            <InputLabel htmlFor='new-password'>Nueva contraseña</InputLabel>
            <OutlinedInput
              id='new-password'
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              inputProps={{ maxLength: 32 }}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={e => e.preventDefault()}
                    edge='end'
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label='Nueva contraseña'
            />
          </FormControl>
          <FormControl
            variant='outlined'
            fullWidth
          >
            <InputLabel htmlFor='confirm-password'>Confirmar contraseña</InputLabel>
            <OutlinedInput
              id='confirm-password'
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              inputProps={{ maxLength: 32 }}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={e => e.preventDefault()}
                    edge='end'
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label='Confirmar contraseña'
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialogs}
            color='primary'
          >
            Cancelar
          </Button>
          <Button
            onClick={handleResetPassword}
            variant='contained'
            sx={buttonLogInCSS}
            disabled={recoveryLoading || !recoveryEmail || !recoveryCode || !newPassword || !confirmPassword}
          >
            {recoveryLoading ? <CircularProgress size={24} /> : 'Restablecer contraseña'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
